/**
 * The "New Azure SQL server (PaaS)" form, as data.
 *
 * The final field list has not been supplied, so the form is built from this table. To add, remove or change a field,
 * edit `PAAS_FIELDS`: the form, its defaults, its validation and the review summary all read from it. Nothing else needs
 * to change. Values are strings, keyed by `key`.
 *
 * What is NOT asked, because it is derived: the server name (see naming.ts), the resource names and the tags.
 */
import type { PaasValues } from '@/types/infrastructure'

export type PaasFieldType = 'text' | 'password' | 'select' | 'number' | 'radio' | 'textarea'

export interface PaasField {
  key: string
  label: string
  type: PaasFieldType
  required: boolean
  help?: string
  options?: { value: string; label: string }[]
  default?: string
  min?: number
  max?: number
  /** Shown only when this returns true. A hidden field is neither validated nor submitted. */
  showWhen?: (values: PaasValues) => boolean
  /** Extra check after the built-in required/min/max checks. Return an error message, or null. */
  validate?: (value: string, values: PaasValues) => string | null
  /** Shown as bullets in the review instead of the raw value (passwords are masked). */
  mask?: boolean
}

/** Logins Azure SQL does not allow as the server admin. */
const RESERVED_LOGINS = ['admin', 'administrator', 'sa', 'root', 'dbmanager', 'loginmanager', 'dbo', 'guest', 'public']

/** At least 12 characters, at least 3 of: upper case, lower case, digit, symbol, and not containing the login. */
export function passwordProblem(password: string, login = ''): string | null {
  if (password.length < 12) return 'Use at least 12 characters.'
  const classes = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((pattern) => pattern.test(password)).length
  if (classes < 3) return 'Use at least 3 of: upper case, lower case, digits, symbols.'
  if (login && password.toLowerCase().includes(login.toLowerCase())) return 'The password must not contain the login.'
  return null
}

const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/

/** One IPv4 address, or one range `start-end`, or a CIDR block. Per line. */
function firewallRuleProblem(line: string): string | null {
  const [first, second, ...rest] = line.split('-').map((part) => part.trim())
  if (rest.length > 0) return `"${line}" is not an address or range.`
  const cidr = /^(.+)\/(\d{1,2})$/.exec(first)
  if (cidr && second === undefined) return IPV4.test(cidr[1]) && Number(cidr[2]) <= 32 ? null : `"${line}" is not a valid CIDR block.`
  if (!IPV4.test(first)) return `"${line}" is not a valid IPv4 address.`
  if (second !== undefined && !IPV4.test(second)) return `"${line}" is not a valid address range.`
  return null
}

export const PAAS_FIELDS: PaasField[] = [
  {
    key: 'adminLogin',
    label: 'Admin login',
    type: 'text',
    required: true,
    showWhen: (v) => v.authMode !== 'entra',
    validate: (value) => {
      if (!/^[A-Za-z][A-Za-z0-9_]{0,127}$/.test(value)) return 'Start with a letter; use letters, digits and underscores only.'
      return RESERVED_LOGINS.includes(value.toLowerCase()) ? `"${value}" is reserved by Azure SQL. Choose another login.` : null
    },
  },
  {
    key: 'adminPassword',
    label: 'Admin password',
    type: 'password',
    required: true,
    mask: true,
    showWhen: (v) => v.authMode !== 'entra',
    validate: (value, v) => passwordProblem(value, v.adminLogin),
  },
  {
    key: 'authMode',
    label: 'Authentication',
    type: 'select',
    required: true,
    default: 'sql',
    options: [
      { value: 'sql', label: 'SQL authentication' },
      { value: 'entra', label: 'Microsoft Entra ID only' },
      { value: 'both', label: 'SQL and Microsoft Entra ID' },
    ],
  },
  {
    key: 'entraAdmin',
    label: 'Entra admin (user or group)',
    type: 'text',
    required: true,
    showWhen: (v) => v.authMode !== 'sql',
    help: 'The user principal name or group name that administers the server.',
    validate: (value) => (value.trim().length >= 3 ? null : 'Enter the Entra user or group.'),
  },
  {
    key: 'serviceTier',
    label: 'Service tier',
    type: 'select',
    required: true,
    default: 'GeneralPurpose',
    options: [
      { value: 'GeneralPurpose', label: 'General Purpose' },
      { value: 'BusinessCritical', label: 'Business Critical' },
      { value: 'Hyperscale', label: 'Hyperscale' },
    ],
  },
  {
    key: 'computeSize',
    label: 'Compute size (vCores)',
    type: 'select',
    required: true,
    default: '4',
    options: ['2', '4', '8', '16', '32'].map((value) => ({ value, label: `${value} vCores` })),
  },
  { key: 'maxStorageGb', label: 'Max storage (GB)', type: 'number', required: true, default: '256', min: 32, max: 4096 },
  {
    key: 'backupRedundancy',
    label: 'Backup storage redundancy',
    type: 'select',
    required: true,
    default: 'Geo',
    options: [
      { value: 'Local', label: 'Locally redundant' },
      { value: 'Zone', label: 'Zone redundant' },
      { value: 'Geo', label: 'Geo redundant' },
      { value: 'GeoZone', label: 'Geo-zone redundant' },
    ],
  },
  {
    key: 'networkAccess',
    label: 'Network access',
    type: 'radio',
    required: true,
    default: 'private',
    options: [
      { value: 'private', label: 'Private endpoint (in the selected VNet and subnet)' },
      { value: 'firewall', label: 'Public endpoint with firewall rules' },
    ],
  },
  {
    key: 'firewallRules',
    label: 'Firewall rules',
    type: 'textarea',
    required: true,
    showWhen: (v) => v.networkAccess === 'firewall',
    help: 'One per line: an IPv4 address, an address range (start-end) or a CIDR block.',
    validate: (value) => {
      const lines = value.split('\n').map((line) => line.trim()).filter(Boolean)
      if (lines.length === 0) return 'Add at least one firewall rule.'
      for (const line of lines) {
        const problem = firewallRuleProblem(line)
        if (problem) return problem
      }
      return null
    },
  },
  { key: 'collation', label: 'Collation', type: 'text', required: false, default: 'SQL_Latin1_General_CP1_CI_AS' },
  {
    key: 'zoneRedundant',
    label: 'Zone redundant',
    type: 'select',
    required: false,
    default: 'no',
    options: [
      { value: 'no', label: 'No' },
      { value: 'yes', label: 'Yes' },
    ],
  },
]

const visible = (field: PaasField, values: PaasValues) => (field.showWhen ? field.showWhen(values) : true)

/** Every field's default. */
export function paasDefaults(): PaasValues {
  return Object.fromEntries(PAAS_FIELDS.map((field) => [field.key, field.default ?? '']))
}

/** The fields to show for these values. */
export function visiblePaasFields(values: PaasValues): PaasField[] {
  return PAAS_FIELDS.filter((field) => visible(field, values))
}

/** Error text per field key. Hidden fields are skipped. An empty record means the form is valid. */
export function validatePaas(values: PaasValues): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const field of visiblePaasFields(values)) {
    const value = (values[field.key] ?? '').toString()
    if (field.required && value.trim() === '') {
      errors[field.key] = `${field.label} is required.`
      continue
    }
    if (value === '') continue
    if (field.type === 'number') {
      const number = Number(value)
      if (!Number.isInteger(number)) errors[field.key] = `${field.label} must be a whole number.`
      else if (field.min !== undefined && number < field.min) errors[field.key] = `${field.label} must be at least ${field.min}.`
      else if (field.max !== undefined && number > field.max) errors[field.key] = `${field.label} must be at most ${field.max}.`
    }
    if (field.options && !field.options.some((option) => option.value === value)) errors[field.key] = `Choose one of the listed options for ${field.label}.`
    if (errors[field.key]) continue
    const problem = field.validate?.(value, values)
    if (problem) errors[field.key] = problem
  }
  return errors
}

/** The values to submit: visible fields only. The password is included exactly as typed; everything else is trimmed. */
export function paasPayloadValues(values: PaasValues): PaasValues {
  return Object.fromEntries(
    visiblePaasFields(values).map((field) => {
      const value = (values[field.key] ?? '').toString()
      return [field.key, field.mask ? value : value.trim()]
    }),
  )
}
